import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Trophy, Users, Copy, CheckCircle, XCircle, ArrowRight,
  ArrowLeft, SpinnerGap, Sword, ShareNetwork, Crown,
  Clock, Target, Link as LinkIcon
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ CREATE CHALLENGE ============
const CreateChallenge = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [questionCount, setQuestionCount] = useState(10);
  const [creating, setCreating] = useState(false);
  const [challengeCode, setChallengeCode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(`${API}/questions/categories`, { withCredentials: true });
        setCategories(data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchCategories();
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/challenges/create`, {
        category: category || null,
        question_count: questionCount,
      }, { withCredentials: true });
      setChallengeCode(data.code);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create challenge');
    } finally {
      setCreating(false);
    }
  };

  const shareUrl = challengeCode ? `${window.location.origin}/challenge/${challengeCode}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };

  if (challengeCode) {
    return (
      <div className="text-center py-12" data-testid="challenge-created">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success/10 flex items-center justify-center">
          <CheckCircle weight="fill" className="w-8 h-8 text-success" />
        </div>
        <h2 className="font-heading font-black text-2xl tracking-tighter mb-2">Challenge Created!</h2>
        <p className="text-muted-foreground mb-6">Share this link with your friends</p>

        <div className="max-w-md mx-auto bg-card border border-border rounded-sm p-4 mb-6">
          <div className="flex items-center gap-2">
            <input
              type="text" readOnly value={shareUrl}
              className="flex-1 bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono"
              data-testid="challenge-share-url"
            />
            <Button onClick={copyLink} size="sm" className="rounded-sm" data-testid="copy-link-button">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex gap-3 justify-center">
          <Link to={`/challenge/${challengeCode}`}>
            <Button className="rounded-sm" data-testid="take-own-challenge">
              <Sword className="w-4 h-4 mr-2" />
              Take This Challenge
            </Button>
          </Link>
          <Button variant="outline" onClick={() => setChallengeCode(null)} className="rounded-sm">
            Create Another
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="create-challenge-form">
      <div className="max-w-md mx-auto">
        <div className="bg-card border border-border rounded-sm p-6 mb-6">
          <h3 className="font-heading font-bold text-lg mb-4">Challenge Settings</h3>

          <div className="mb-4">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-2">
              Category
            </label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm"
              data-testid="challenge-category-select"
            >
              <option value="">Mixed (Random)</option>
              {categories.filter(c => !c.locked).map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.question_count})</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-2">
              Number of Questions
            </label>
            <div className="flex items-center gap-3">
              {[5, 10, 15, 20].map(n => (
                <button
                  key={n}
                  onClick={() => setQuestionCount(n)}
                  className={cn(
                    "px-4 py-2 rounded-sm border text-sm font-bold flex-1",
                    questionCount === n
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-border hover:border-primary/50"
                  )}
                  data-testid={`question-count-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={handleCreate}
            disabled={creating}
            className="w-full rounded-sm"
            data-testid="create-challenge-button"
          >
            {creating ? (
              <SpinnerGap className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Sword className="w-4 h-4 mr-2" />
            )}
            Create Challenge
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============ TAKE CHALLENGE (QUIZ) ============
const TakeChallenge = ({ code }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [stage, setStage] = useState('intro'); // intro, quiz, results
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    fetchChallenge();
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChallenge = async () => {
    try {
      const [infoRes, questionsRes] = await Promise.all([
        axios.get(`${API}/challenges/${code}`),
        axios.get(`${API}/challenges/${code}/questions`),
      ]);
      setInfo(infoRes.data);
      setQuestions(questionsRes.data.questions);
    } catch (err) {
      toast.error('Challenge not found');
      navigate('/challenge');
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = () => {
    if (!user && !guestName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    setStage('quiz');
    setStartTime(Date.now());
  };

  const selectAnswer = (questionId, answerIdx) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerIdx }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const timeTaken = Math.round((Date.now() - startTime) / 1000);
    const answerList = questions.map(q => ({
      question_id: q.id,
      selected_answer: answers[q.id] ?? -1,
    }));

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (user) headers['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;

      const { data } = await axios.post(`${API}/challenges/${code}/submit`, {
        answers: answerList,
        guest_name: user ? user.name : guestName,
        time_taken: timeTaken,
      }, { headers, withCredentials: true });

      setResults(data);
      setStage('results');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // ---- INTRO ----
  if (stage === 'intro') {
    return (
      <div className="max-w-md mx-auto text-center py-8" data-testid="challenge-intro">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-accent/10 flex items-center justify-center">
          <Sword weight="fill" className="w-8 h-8 text-accent" />
        </div>
        <h2 className="font-heading font-black text-2xl tracking-tighter mb-2">
          {info?.creator_name}'s Challenge
        </h2>
        <p className="text-muted-foreground mb-6">
          {info?.question_count} questions
          {info?.category ? ` on ${info.category.replace(/_/g, ' ')}` : ' (mixed topics)'}
        </p>

        {!user && (
          <div className="mb-6">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-2">
              Your Name
            </label>
            <input
              type="text"
              value={guestName}
              onChange={e => setGuestName(e.target.value)}
              placeholder="Enter your name"
              className="w-full max-w-xs mx-auto bg-background border border-border rounded-sm px-3 py-2 text-sm text-center"
              data-testid="guest-name-input"
            />
          </div>
        )}

        <Button onClick={startQuiz} className="rounded-sm" data-testid="start-challenge-button">
          <Sword className="w-4 h-4 mr-2" />
          Start Challenge
        </Button>

        {!user && (
          <p className="mt-4 text-xs text-muted-foreground">
            <Link to="/login" className="text-primary hover:underline">Log in</Link> to track your score
          </p>
        )}
      </div>
    );
  }

  // ---- QUIZ ----
  if (stage === 'quiz') {
    const q = questions[currentIdx];
    const answered = answers[q.id] !== undefined;
    const allAnswered = Object.keys(answers).length === questions.length;

    return (
      <div className="max-w-2xl mx-auto" data-testid="challenge-quiz">
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm font-mono text-muted-foreground">
            {currentIdx + 1} / {questions.length}
          </span>
          <Progress value={((currentIdx + 1) / questions.length) * 100} className="flex-1 mx-4 h-2 rounded-none" />
          <span className="text-xs text-muted-foreground">{info?.creator_name}'s challenge</span>
        </div>

        <div className="bg-card border border-border rounded-sm p-6 mb-4">
          {q.image_url && (
            <img src={q.image_url} alt="" className="max-h-40 mx-auto mb-4 rounded-sm" />
          )}
          <h3 className="font-heading font-bold text-lg mb-6">{q.question_text}</h3>
          <div className="space-y-3">
            {q.options.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => selectAnswer(q.id, idx)}
                className={cn(
                  "w-full text-left p-4 rounded-sm border transition-all",
                  answers[q.id] === idx
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                )}
                data-testid={`option-${idx}`}
              >
                <span className="font-mono text-xs text-muted-foreground mr-3">
                  {String.fromCharCode(65 + idx)}
                </span>
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-between">
          <Button
            variant="outline" onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
            disabled={currentIdx === 0} className="rounded-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>

          {currentIdx < questions.length - 1 ? (
            <Button onClick={() => setCurrentIdx(currentIdx + 1)} disabled={!answered} className="rounded-sm">
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={!allAnswered || submitting} className="rounded-sm bg-success hover:bg-success/90" data-testid="submit-challenge">
              {submitting ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Submit
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ---- RESULTS ----
  if (stage === 'results') {
    return (
      <div className="max-w-2xl mx-auto" data-testid="challenge-results">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
            <Trophy weight="fill" className="w-8 h-8 text-accent" />
          </div>
          <h2 className="font-heading font-black text-3xl tracking-tighter mb-1">
            {results.score}/{results.total}
          </h2>
          <p className="text-muted-foreground">{results.percentage}% correct</p>
        </div>

        <div className="flex gap-3 justify-center mb-8">
          <Link to={`/challenge/${code}/results`}>
            <Button className="rounded-sm" data-testid="view-leaderboard">
              <Users className="w-4 h-4 mr-2" /> View Leaderboard
            </Button>
          </Link>
          <Button variant="outline" onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/challenge/${code}`);
            toast.success('Link copied!');
          }} className="rounded-sm">
            <ShareNetwork className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        {/* Answer Review */}
        <div className="space-y-3">
          {results.results.map((r, idx) => (
            <div key={idx} className={cn(
              "bg-card border rounded-sm p-4",
              r.correct ? "border-success/30" : "border-destructive/30"
            )}>
              <div className="flex items-start gap-2 mb-2">
                {r.correct ? <CheckCircle weight="fill" className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                           : <XCircle weight="fill" className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />}
                <span className="text-sm font-medium">{questions[idx]?.question_text}</span>
              </div>
              {!r.correct && (
                <p className="text-xs text-muted-foreground ml-7">
                  Correct: {questions[idx]?.options[r.correct_answer]}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

// ============ LEADERBOARD ============
const Leaderboard = ({ code }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/challenges/${code}/results`);
        setData(data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [code]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!data) return <p className="text-center text-muted-foreground py-20">Challenge not found</p>;

  return (
    <div className="max-w-lg mx-auto" data-testid="challenge-leaderboard">
      <div className="text-center mb-8">
        <h2 className="font-heading font-black text-2xl tracking-tighter mb-1">
          {data.creator_name}'s Challenge
        </h2>
        <p className="text-sm text-muted-foreground">
          {data.question_count} questions
          {data.category ? ` — ${data.category.replace(/_/g, ' ')}` : ''}
        </p>
      </div>

      {data.leaderboard.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border rounded-sm">
          <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">No one has taken this challenge yet.</p>
          <Link to={`/challenge/${code}`}>
            <Button className="mt-4 rounded-sm" data-testid="be-first">
              <Sword className="w-4 h-4 mr-2" /> Be the First!
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {data.leaderboard.map((entry, idx) => (
            <div key={idx} className={cn(
              "bg-card border rounded-sm p-4 flex items-center gap-4",
              idx === 0 ? "border-accent/40" : "border-border"
            )} data-testid={`leaderboard-entry-${idx}`}>
              <div className={cn(
                "w-10 h-10 rounded-sm flex items-center justify-center font-heading font-black text-lg flex-shrink-0",
                idx === 0 ? "bg-accent/20 text-accent" :
                idx === 1 ? "bg-blue-500/20 text-blue-400" :
                idx === 2 ? "bg-amber-500/20 text-amber-500" :
                "bg-muted text-muted-foreground"
              )}>
                {idx === 0 ? <Crown weight="fill" className="w-5 h-5" /> : entry.rank}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold truncate">{entry.user_name}</span>
                  {entry.is_creator && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-accent/10 text-accent px-2 py-0.5 rounded-sm">
                      Creator
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {entry.time_taken ? `${Math.floor(entry.time_taken / 60)}m ${entry.time_taken % 60}s` : '—'}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span className="font-heading font-black text-xl">{entry.score}</span>
                <span className="text-muted-foreground text-sm">/{entry.total}</span>
                <p className="text-xs text-muted-foreground">{entry.percentage}%</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3 justify-center mt-6">
        <Link to={`/challenge/${code}`}>
          <Button variant="outline" className="rounded-sm">
            <Sword className="w-4 h-4 mr-2" /> Take Challenge
          </Button>
        </Link>
        <Button variant="outline" onClick={() => {
          navigator.clipboard.writeText(`${window.location.origin}/challenge/${code}`);
          toast.success('Link copied!');
        }} className="rounded-sm">
          <Copy className="w-4 h-4 mr-2" /> Share
        </Button>
      </div>
    </div>
  );
};

// ============ MY CHALLENGES ============
const MyChallenges = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/challenges/my/list`, { withCredentials: true });
        setData(data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerGap className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  const allChallenges = [...(data?.created || []), ...(data?.participated || [])];

  if (allChallenges.length === 0) return null;

  return (
    <div className="mt-8" data-testid="my-challenges">
      <h3 className="font-heading font-bold text-lg mb-4">Your Challenges</h3>
      <div className="space-y-2">
        {allChallenges.map(c => (
          <Link key={c.code} to={`/challenge/${c.code}/results`} className="block">
            <div className="bg-card border border-border rounded-sm p-4 card-hover flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm">{c.creator_name}'s Challenge</span>
                  <span className="text-[10px] font-mono bg-muted px-2 py-0.5 rounded-sm text-muted-foreground">{c.code}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {c.question_count}q {c.category ? `— ${c.category.replace(/_/g, ' ')}` : '— mixed'}
                  {' '}&bull; {c.total_attempts} attempt{c.total_attempts !== 1 ? 's' : ''}
                </p>
              </div>
              {c.my_score !== null && c.my_score !== undefined && (
                <div className="text-right">
                  <span className="font-heading font-black text-lg">{c.my_score}</span>
                  <span className="text-muted-foreground text-sm">/{c.my_total}</span>
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

// ============ MAIN PAGE ============
export const ChallengePage = () => {
  const { code, view } = useParams();
  const { user } = useAuth();

  // Leaderboard view
  if (code && view === 'results') {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <Leaderboard code={code} />
        </div>
      </div>
    );
  }

  // Take a challenge
  if (code) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <TakeChallenge code={code} />
        </div>
      </div>
    );
  }

  // Create challenge / home
  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="challenge-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
            Compete
          </p>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
            Challenge a Friend
          </h1>
          <p className="text-muted-foreground mt-2">
            Create a quiz, share the link, and compare scores.
          </p>
        </div>

        <CreateChallenge />

        {user && <MyChallenges />}
      </div>
    </div>
  );
};
